import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useUserRole } from "@/hooks/useUserRole";
import { useSubscription, SUBSCRIPTION_TIERS, formatDiscountedPrice } from "@/hooks/useSubscription";
import { usePromo } from "@/hooks/usePromo";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { supabase } from "@/integrations/supabase/client";

const tiers = [
  {
    name: "Pro",
    price: "$59",
    promoPrice: "$29",
    period: "/mo",
    role: "pro",
    recommended: true,
    description: "For the individual closer.",
    features: [
      "7-day free trial",
      "Unlimited personal leads",
      "Google Calendar sync",
      "Telegram bot integration",
      "Voice-to-CRM AI assistant",
      "Visual Gantt timeline",
      "CSV import & export",
    ],
    cta: "Start 7-day free trial",
  },
  {
    name: "Business",
    price: "$100",
    period: "/user/mo",
    role: "business",
    description: "For sales teams that collaborate.",
    features: [
      "Everything in Pro",
      "7-day free trial",
      "Unlimited group workspaces",
      "Team collaboration & permissions",
      "Shared pipelines & calendars",
      "Priority support",
      "Custom onboarding",
    ],
    cta: "Start 7-day free trial",
  },
];

const Pricing = () => {
  const navigate = useNavigate();
  const { role } = useUserRole();
  const sub = useSubscription();
  const { createCheckout, openCustomerPortal, subscribed } = sub;
  const { isPromoActive, offer } = usePromo();
  const [processingTier, setProcessingTier] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("success")) {
      toast.success("Subscription activated", { description: "Refresh to see your new plan." });
      window.history.replaceState({}, "", "/pricing");
      // Send the welcome email once, now that the card-backed trial exists.
      supabase.auth.getUser().then(({ data }) => {
        const u = data.user;
        if (!u?.email) return;
        const key = `welcome_sent_${u.id}`;
        if (localStorage.getItem(key)) return;
        localStorage.setItem(key, "1");
        supabase.functions
          .invoke("send-welcome-email", {
            body: { email: u.email, fullName: (u.user_metadata?.full_name as string) ?? "" },
          })
          .catch((err) => console.warn("Welcome email failed:", err));
      });
    }
    if (params.get("start") === "trial") {
      toast("Choose a plan to start your 7-day free trial", {
        description: "A card is required. You won't be charged until the trial ends.",
      });
      window.history.replaceState({}, "", "/pricing");
    }
    if (params.get("canceled")) {
      toast.error("Checkout canceled");
      window.history.replaceState({}, "", "/pricing");
    }
  }, []);

  // Recommended first on mobile, normal order on desktop
  const ordered = [...tiers].sort((a, b) => (b.recommended ? 1 : 0) - (a.recommended ? 1 : 0));

  return (
    <>
      <Helmet>
        <title>Pricing | EchoCRM</title>
        <meta name="description" content="Simple, transparent pricing for EchoCRM. Pro and Business plans, each with a 7-day free trial." />
        <link rel="canonical" href="https://echocrm.app/pricing" />
      </Helmet>

      <main className="min-h-screen bg-background text-foreground">
        <MarketingNav />

        <section className="theme-glow">
          <div className="mx-auto max-w-6xl px-5 sm:px-8 lg:px-12 pt-16 sm:pt-24 pb-12 sm:pb-16">
            <p className="text-[12px] font-mono-accent uppercase tracking-[0.18em] text-muted-foreground mb-4">Pricing</p>
            <h1 className="font-display font-semibold text-[36px] sm:text-[56px] lg:text-[64px] tracking-[-0.035em] leading-[1.05] max-w-3xl">
              Start free for 7 days.<br className="hidden sm:block" /> Pay only if it earns its keep.
            </h1>
            <p className="mt-5 text-[16px] sm:text-[18px] text-muted-foreground max-w-2xl">
              Both plans start with a 7-day free trial. Cancel anytime before the trial ends and you're not charged.
            </p>
            {isPromoActive && offer && (
              <p className="mt-4 inline-flex items-center rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-[13px] text-primary">
                {offer.headline}
              </p>
            )}
          </div>
        </section>

        <section>
          <div className="mx-auto max-w-6xl px-5 sm:px-8 lg:px-12 pb-20 sm:pb-28">
            {/* Mobile: stack with recommended first */}
            <div className="grid grid-cols-1 lg:hidden gap-4">
              {ordered.map((t) => (
                <PricingCard
                  key={t.role}
                  tier={t}
                  promo={isPromoActive}
                  promoLabel={offer?.priceLabel}
                  current={role === t.role}
                  processing={processingTier === t.role}
                  onSelect={async () => {
                    setProcessingTier(t.role);
                    const cfg = SUBSCRIPTION_TIERS[t.role as keyof typeof SUBSCRIPTION_TIERS];
                    const url = await createCheckout(cfg.priceId);
                    if (url) window.open(url, "_blank");
                    setProcessingTier(null);
                  }}
                  onManage={openCustomerPortal}
                  subscribed={subscribed}
                  yourPrice={role === t.role ? formatDiscountedPrice(sub, t.role === "business" ? " per user" : "") : null}
                />
              ))}
            </div>

            {/* Desktop: 2-column natural order */}
            <div className="hidden lg:grid grid-cols-2 gap-px bg-border rounded-lg overflow-hidden border border-border max-w-4xl mx-auto">
              {tiers.map((t) => (
                <PricingCard
                  key={t.role}
                  tier={t}
                  inGrid
                  promo={isPromoActive}
                  promoLabel={offer?.priceLabel}
                  current={role === t.role}
                  processing={processingTier === t.role}
                  onSelect={async () => {
                    setProcessingTier(t.role);
                    const cfg = SUBSCRIPTION_TIERS[t.role as keyof typeof SUBSCRIPTION_TIERS];
                    const url = await createCheckout(cfg.priceId);
                    if (url) window.open(url, "_blank");
                    setProcessingTier(null);
                  }}
                  onManage={openCustomerPortal}
                  subscribed={subscribed}
                  yourPrice={role === t.role ? formatDiscountedPrice(sub, t.role === "business" ? " per user" : "") : null}
                />
              ))}
            </div>

            {/* FAQ */}
            <div className="mt-20 sm:mt-28 max-w-3xl">
              <p className="text-[12px] font-mono-accent uppercase tracking-[0.18em] text-muted-foreground mb-4">FAQ</p>
              <h2 className="font-display font-semibold text-[24px] sm:text-[32px] tracking-[-0.02em] mb-8">Common questions</h2>
              <div className="divide-y divide-border border-y border-border">
                {[
                  { q: "How does the free trial work?", a: "Every new account gets 7 days of full access on the plan you pick. A card is collected at checkout but nothing is charged during the trial — cancel any time before it ends and you pay nothing." },
                  { q: "What happens when the trial ends?", a: "If you don't cancel, billing starts at your plan's rate ($59/mo for Pro, $100/mo per user for Business). If you cancel, your account moves to read-only until you resubscribe — your data stays intact." },
                  { q: "How does the lifetime deal work?", a: "If you arrive through our current campaign link, Pro is $29/mo instead of $59/mo, and that rate is locked in for as long as your subscription stays continuously active. The offer applies to new subscriptions only, can be withdrawn at any time, and can't be combined with other discounts." },
                  { q: "Can I change plans anytime?", a: "Yes. Upgrade to Business (or downgrade back to Pro) from the customer portal at any time; the change applies at your next billing cycle." },
                  { q: "What happens to my data if I downgrade?", a: "Your data is preserved. You'll just see the access limits of the lower tier." },
                  { q: "Do you offer annual billing?", a: "Reach out to support@echocrm.app and we'll set you up." },
                ].map((f) => (
                  <details key={f.q} className="group py-5">
                    <summary className="flex items-center justify-between cursor-pointer list-none">
                      <span className="text-[15px] font-medium">{f.q}</span>
                      <span className="text-muted-foreground group-open:rotate-45 transition-transform text-lg leading-none">+</span>
                    </summary>
                    <p className="mt-3 text-[14px] text-muted-foreground leading-[1.55]">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>

        <MarketingFooter />
      </main>
    </>
  );
};

function PricingCard({
  tier, current, processing, onSelect, onManage, subscribed, inGrid, promo, promoLabel, yourPrice,
}: {
  tier: typeof tiers[number];
  current: boolean;
  processing: boolean;
  onSelect: () => void;
  onManage: () => void;
  subscribed: boolean;
  inGrid?: boolean;
  promo?: boolean;
  promoLabel?: string;
  yourPrice?: string | null;
}) {
  const showPromo = Boolean(promo && "promoPrice" in tier && tier.promoPrice);
  return (
    <div
      className={`relative bg-background p-6 sm:p-7 flex flex-col ${
        inGrid ? "" : "rounded-lg border border-border"
      } ${tier.recommended ? (inGrid ? "ring-1 ring-inset ring-primary" : "border-primary") : ""}`}
    >
      {tier.recommended && (
        <span className="absolute top-4 right-4 text-[10px] font-mono-accent uppercase tracking-wider text-primary">
          Recommended
        </span>
      )}
      <h3 className="font-display font-semibold text-[18px]">{tier.name}</h3>
      <p className="text-[13px] text-muted-foreground mt-1">{tier.description}</p>
      <div className="mt-5 flex items-baseline gap-2">
        {showPromo && (
          <span className="font-display text-[22px] font-medium text-muted-foreground line-through">{tier.price}</span>
        )}
        <span className="font-display text-[36px] font-semibold tracking-[-0.02em]">
          {showPromo ? (promoLabel ?? (tier as { promoPrice?: string }).promoPrice) : tier.price}
        </span>
        <span className="text-[13px] text-muted-foreground">{tier.period}</span>
      </div>
      {showPromo && (
        <p className="mt-1 text-[12px] font-mono-accent uppercase tracking-wider text-primary">
          Limited-time offer
        </p>
      )}

      <ul className="mt-6 space-y-2.5 flex-1">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-[13px]">
            <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
            <span className="text-foreground/90">{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-7 flex flex-col gap-2">
        <Button
          className="w-full"
          variant={tier.recommended ? "default" : "outline"}
          disabled={current || processing}
          onClick={onSelect}
        >
          {processing ? "Processing…" : current ? "Current plan" : tier.cta}
          {!current && !processing && <ArrowRight className="h-4 w-4" />}
        </Button>
        {current && yourPrice && (
          <p className="text-center text-[12px] text-primary font-mono-accent">Your plan: {yourPrice}</p>
        )}
        {current && subscribed && (
          <Button variant="ghost" size="sm" className="w-full" onClick={onManage}>
            Manage subscription
          </Button>
        )}
      </div>
    </div>
  );
}

export default Pricing;